import { useState } from 'react';
import { FileText, Download, AlertCircle, File, Image } from 'lucide-react';
import { usePortalDocuments, usePortalDownloadDocument } from '@/hooks/usePortalDocuments';
import type { PortalDocumentItem } from '@/hooks/usePortalDocuments';

/**
 * Patient portal documents page.
 * Shows documents shared with the patient and allows downloads.
 */
export function PortalDocuments() {
  const { data: documents = [], isLoading, error } = usePortalDocuments();
  const downloadMutation = usePortalDownloadDocument();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (doc: PortalDocumentItem) => {
    setDownloadingId(doc.id);
    try {
      const url = await downloadMutation.mutateAsync(doc.id);
      
      // Open the download URL in a new tab
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download document:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <div className="flex items-center text-red-800 dark:text-red-200">
          <AlertCircle className="mr-2 h-5 w-5" />
          <span>Failed to load documents. Please try again.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Documents</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          View and download documents shared with you by your clinics
        </p>
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No documents available
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Your clinic will share documents with you here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((document) => (
            <DocumentCard
              key={document.id}
              document={document}
              onDownload={handleDownload}
              isDownloading={downloadingId === document.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface DocumentCardProps {
  document: PortalDocumentItem;
  onDownload: (document: PortalDocumentItem) => void;
  isDownloading: boolean;
}

function DocumentCard({ document, onDownload, isDownloading }: DocumentCardProps) {
  const isImage = document.mime_type.startsWith('image/');
  const isPdf = document.mime_type === 'application/pdf';

  const Icon = isImage ? Image : isPdf ? FileText : File;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between">
        <div className="flex flex-1 items-start space-x-4">
          {/* Icon */}
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
            <Icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>

          {/* Document Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {document.original_filename}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {document.clinic_name}
            </p>
            
            {/* Tags */}
            {document.tags && document.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {document.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            
            {/* Notes */}
            {document.notes && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {document.notes}
              </p>
            )}

            {/* Metadata */}
            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span>{formatFileSize(document.size_bytes)}</span>
              <span>•</span>
              <span>{formatDate(document.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Download Button */}
        <button
          onClick={() => onDownload(document)}
          disabled={isDownloading}
          className="ml-4 flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDownloading ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
              Downloading...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Download
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
